import ImageProvider, { ProvidedImage } from "./ImageProvider"
import { Utils } from "../../Utils"
import Constants from "../../Models/Constants"
import { LicenseInfo } from "./LicenseInfo"
import { ServerSourceInfo } from "../../Models/SourceOverview"

export class Imgur extends ImageProvider {
    public static readonly defaultValuePrefix = ["https://i.imgur.com"]
    public static readonly singleton = new Imgur()
    public readonly name = "Imgur"
    public readonly defaultKeyPrefixes: string[] = ["image"]
    public static readonly apiUrl = "https://api.imgur.com/3/image/"
    public static readonly apiUrlInfo: ServerSourceInfo = {
        url: Imgur.apiUrl,
        description:
            "[Legacy] If an image is stored on imgur, read the description and attempt to get a license out of it. Most (if not all) have moved",
        category: "core",
        trigger: ["specific_feature"],
    }

    private constructor() {
        super()
    }

    apiUrls(): ServerSourceInfo[] {
        return [Imgur.apiUrlInfo]
    }

    sourceIcon() {
        return undefined
    }

    public ExtractUrls(key: string, value: string): undefined | ProvidedImage[] {
        if (Imgur.defaultValuePrefix.some((prefix) => value.startsWith(prefix))) {
            return [
                {
                    url: value,
                    key: key,
                    provider: this,
                    id: value,
                    isSpherical: false,
                    originalAttribute: { key, value },
                },
            ]
        }
        return undefined
    }

    public static parseLicense(descr: string) {
        const data: Record<string, string> = {}

        if (!descr) {
            return undefined
        }
        if (descr.toLowerCase() === "cc0") {
            data.author = "Unknown"
            data.license = "CC0"
        } else {
            for (const tag of descr.split("\n")) {
                const kv = tag.split(":")
                if (kv.length < 2) {
                    continue
                }
                const k = kv[0]
                data[k] = kv[1]?.replace(/\r/g, "")
            }
        }
        if (Object.keys(data).length === 0) {
            return undefined
        }

        const licenseInfo = new LicenseInfo()

        licenseInfo.licenseShortName = data.license
        licenseInfo.artist = data.author

        return licenseInfo
    }

    /**
     * Download the attribution and license info for the picture at the given URL
     *
     * const data = {"data":{"id":"I9t6B7B","title":"Station Knokke","description":"author:Pieter Vander Vennet\r\nlicense:CC-BY 4.0\r\nosmid:node\/9812712386","datetime":1655052078,"type":"image\/jpeg","animated":false,"width":2400,"height":1795,"size":910872,"views":2,"bandwidth":1821744,"vote":null,"favorite":false,"nsfw":false,"section":null,"account_url":null,"account_id":null,"is_ad":false,"in_most_viral":false,"has_sound":false,"tags":[],"ad_type":0,"ad_url":"","edited":"0","in_gallery":false,"link":"https:\/\/i.imgur.com\/I9t6B7B.jpg","ad_config":{"safeFlags":["not_in_gallery","share"],"highRiskFlags":[],"unsafeFlags":["sixth_mod_unsafe"],"wallUnsafeFlags":[],"showsAds":false,"showAdLevel":1}},"success":true,"status":200}
     * Utils.injectJsonDownloadForTests("https://api.imgur.com/3/image/E0RuAK3", data)
     * const licenseInfo = await Imgur.singleton.DownloadAttribution({id: "https://i.imgur.com/E0RuAK3.jpg"})
     * const expected = new LicenseInfo()
     * expected.licenseShortName = "CC-BY 4.0"
     * expected.artist = "Pieter Vander Vennet"
     * expected.date = new Date(1655052078000)
     * expected.views = 2
     * licenseInfo // => expected
     * const licenseInfoJpeg = await Imgur.singleton.DownloadAttribution({id:"https://i.imgur.com/E0RuAK3.jpeg"})
     * licenseInfoJpeg // => expected
     * const licenseInfoUpperCase = await Imgur.singleton.DownloadAttribution({id: "https://i.imgur.com/E0RuAK3.JPEG"})
     * licenseInfoUpperCase // => expected
     *
     *
     */
    public async DownloadAttribution(
        providedImage: {
            id: string
        },
        withResponse?: (obj) => void
    ): Promise<LicenseInfo> {
        const url = providedImage.id
        const hash = url.substr("https://i.imgur.com/".length).split(/(\.jpe?g)|(\.png)/i)[0]

        const apiUrl = Imgur.apiUrl + hash
        const response = await Utils.downloadJsonCached<{
            data: { description: string; datetime: string; views: number }
        }>(apiUrl, 365 * 24 * 60 * 60, {
            Authorization: "Client-ID " + Constants.ImgurApiKey,
        })
        if (withResponse) {
            withResponse(response)
        }

        const imgurData = response.data
        const license = Imgur.parseLicense(imgurData.description ?? "")
        if (license) {
            license.views = imgurData.views
            license.date = new Date(Number(imgurData.datetime) * 1000)
        }

        return license
    }

    getPanoramaInfo(): undefined {
        return undefined
    }

    visitUrl(): string | undefined {
        return undefined
    }
}
